import { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import { hashPassword } from "../../common/api/auth";
import { useToast } from "../../hooks/useToast";

const ROLE_APPS = ["Web", "BiConnectPro", "dbAdmin"];

function ModalNewUser({ groups = [], clients = [], defaultClient = "", onClose, onCreate }) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [roles, setRoles] = useState([{ app: ROLE_APPS[0], role: "" }]);

  const toast = useToast();

  const availableClients = useMemo(() => {
    if (!clients || clients.length === 0) {
      return defaultClient !== undefined && defaultClient !== null
        ? [{ COD_CLIENTE: defaultClient, NOMBRE_CLIENTE: "" }]
        : [];
    }

    return clients.map((client) => ({
      COD_CLIENTE: client.COD_CLIENTE ?? "",
      NOMBRE_CLIENTE:
        client.NOMBRE_CLIENTE ?? client.NOMBRE ?? client.CLIENTE ?? "",
    }));
  }, [clients, defaultClient]);

  useEffect(() => {
    if (selectedClients.length > 0) return;

    const codes = availableClients.map((c) => c.COD_CLIENTE ?? "");
    if (codes.length > 0) {
      const hasDefault =
        defaultClient !== undefined && defaultClient !== null &&
        codes.includes(defaultClient);
      const chosen = hasDefault ? defaultClient : codes[0];
      if (chosen !== undefined) {
        setSelectedClients([chosen]);
      }
      return;
    }

    if (defaultClient !== undefined && defaultClient !== null) {
      setSelectedClients([defaultClient]);
    }
  }, [availableClients, defaultClient, selectedClients.length]);

  const toggleGroup = (groupName) => {
    setSelectedGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((g) => g !== groupName)
        : [...prev, groupName]
    );
  };

  const toggleClient = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((c) => c !== clientId)
        : [...prev, clientId]
    );
  };

  const handleRoleChange = (index, field, value) => {
    setRoles((prev) =>
      prev.map((role, idx) =>
        idx === index
          ? {
              ...role,
              [field]: value,
            }
          : role
      )
    );
  };

  const handleAddRole = () => {
    setRoles((prev) => [...prev, { app: ROLE_APPS[0], role: "" }]);
  };

  const handleRemoveRole = (index) => {
    setRoles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async () => {
    if (!username.trim()) {
      toast.error("El nombre de usuario es obligatorio.");
      return;
    }

    if (!email.trim()) {
      toast.error("El email es obligatorio.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Introduce un email válido.");
      return;
    }

    if (!password.trim() || !confirmPassword.trim()) {
      toast.error("La contraseña es obligatoria.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }

    if (selectedClients.length === 0) {
      toast.error("Debes asociar el usuario al menos a un cliente.");
      return;
    }

    const sanitizedRoles = roles
      .map((item) => ({
        App: item.app,
        Rol: item.role.trim(),
      }))
      .filter((item) => item.App && item.Rol);

    if (sanitizedRoles.length === 0) {
      toast.error("Debes asignar al menos un rol al usuario.");
      return;
    }

    try {
      const hashedPassword = await hashPassword(password);

      const clientObjects = selectedClients.map((code) => {
        const match = availableClients.find(
          (client) => (client.COD_CLIENTE ?? "") === code
        );

        return {
          COD_CLIENTE: code,
          NOMBRE_CLIENTE: match?.NOMBRE_CLIENTE ?? "",
        };
      });

      const primaryClient = clientObjects[0] ?? {
        COD_CLIENTE: "",
        NOMBRE_CLIENTE: "",
      };

      const payload = {
        USER: username.trim(),
        EMAIL: email.trim(),
        ACTIVO: enabled ? 1 : 0,
        NEW_PASS_HASH: hashedPassword,
        PASS_HASH: hashedPassword,
        GRUPOS: selectedGroups,
        ROLES: sanitizedRoles,
        COD_CLIENTE: primaryClient.COD_CLIENTE,
        NOMBRE_CLIENTE: primaryClient.NOMBRE_CLIENTE,
        CLIENTES_ASOCIADOS: clientObjects,
      };

      const success = await onCreate(payload);

      if (success) {
        toast.success("Usuario creado correctamente.");
        onClose();
      } else {
        toast.error("No se pudo crear el usuario. Revisa los datos ingresados.");
      }
    } catch (err) {
      console.error("Error creando usuario", err);
      toast.error(err.message || "Error inesperado al crear el usuario.");
    }
  };

  return (
    <Modal title="Nuevo usuario" onClose={onClose}>
      <div className="input-group">
        <label className="label">Nombre de usuario</label>
        <input
          className="input"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>

      <div className="input-group">
        <label className="label">Email</label>
        <input
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="input-group">
        <label className="label">Contraseña</label>
        <input
          className="input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      <div className="input-group">
        <label className="label">Confirmar contraseña</label>
        <input
          className="input"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </div>

      <div className="input-group_check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label>Usuario habilitado</label>
      </div>

      <div className="input-group">
        <label className="label">Clientes asociados</label>
        <div className="lista">
          {availableClients.map((client, index) => {
            const code = client.COD_CLIENTE ?? "";
            const name = client.NOMBRE_CLIENTE || "Sin nombre";

            return (
              <span key={`${code}-${index}`}>
                <input
                  type="checkbox"
                  checked={selectedClients.includes(code)}
                  onChange={() => toggleClient(code)}
                />
                {name}
              </span>
            );
          })}
        </div>
      </div>

      <div className="input-group">
        <label className="label">Grupos</label>
        <div className="lista">
          {groups.map((group) => (
            <span key={group}>
              <input
                type="checkbox"
                checked={selectedGroups.includes(group)}
                onChange={() => toggleGroup(group)}
              />
              {group}
            </span>
          ))}
        </div>
      </div>

      <div className="input-group">
        <label className="label">Roles</label>
        <div className="lista">
          {roles.map((role, index) => (
            <div key={`role-${index}`} className="input-group_row" style={{ gap: "0.5rem", alignItems: "center" }}>
              <select
                className="input"
                value={role.app}
                onChange={(e) => handleRoleChange(index, "app", e.target.value)}
              >
                {ROLE_APPS.map((app) => (
                  <option key={app} value={app}>
                    {app}
                  </option>
                ))}
              </select>
              <input
                className="input"
                type="text"
                placeholder="Rol"
                value={role.role}
                onChange={(e) => handleRoleChange(index, "role", e.target.value)}
              />
              {roles.length > 1 && (
                <span className="boton_cancelar" onClick={() => handleRemoveRole(index)}>
                  Quitar
                </span>
              )}
            </div>
          ))}
          <span className="boton_opciones" onClick={handleAddRole}>
            Añadir rol
          </span>
        </div>
      </div>

      <div className="input-group_row" style={{ justifyContent: "center", gap: "1rem" }}>
        <span className="boton_opciones" onClick={handleSubmit}>
          Crear usuario
        </span>
        <span className="boton_cancelar" onClick={onClose}>
          Cancelar
        </span>
      </div>
    </Modal>
  );
}

export default ModalNewUser;